import { ArrowRight, Zap, Building2, Users, CreditCard, BarChart3, Bell, Shield, Wifi, Target } from "lucide-react";
import { useState, useEffect } from "react";
import { useFlexOfficeCTA } from "./useFlexOfficeCTA";
import data from "../data/flexOfficeData.json";

const ICON_MAP = { Building2, Users, CreditCard, BarChart3, Bell, Shield, Wifi, Target };

const useBreakpoint = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { isMobile: w < 640, isTablet: w >= 640 && w < 1024 };
};

const FlexOfficeFeatures = () => {
  const cta = useFlexOfficeCTA();
  const { isMobile, isTablet } = useBreakpoint();
  const d = data.features;

  return (
    <section style={{ background: "white", padding: isMobile ? "60px 20px" : isTablet ? "72px 32px" : "96px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 40 : 64 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eff6ff", border: "1px solid #bfdbfe", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
            <Zap style={{ width: 14, height: 14, color: "#1d4ed8" }} />
            <span style={{ color: "#1d4ed8", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{d.badge}</span>
          </div>
          <h2 style={{ fontSize: isMobile ? 28 : isTablet ? 38 : 48, fontWeight: 900, letterSpacing: "-0.02em", color: "#0f172a", lineHeight: 1.1 }}>
            {d.headline} <span style={{ color: "#1d4ed8" }}>{d.headlineAccent}</span>
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 18, color: "#64748b", maxWidth: 560, margin: "16px auto 0", lineHeight: 1.7 }}>{d.description}</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: isMobile ? 12 : 20 }}>
          {d.items.map((f, i) => {
            const IconComp = ICON_MAP[f.icon];
            return (
              <div key={i}
                style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 18, padding: isMobile ? 18 : 28, cursor: "default", position: "relative", overflow: "hidden", transition: "all 0.3s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "white"; e.currentTarget.style.borderColor = "#bfdbfe"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(29,78,216,0.1)"; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.querySelector(".accent-bar").style.transform = "scaleX(1)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; e.currentTarget.querySelector(".accent-bar").style.transform = "scaleX(0)"; }}>
                <div style={{ width: isMobile ? 40 : 52, height: isMobile ? 40 : 52, background: "#eff6ff", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", color: f.color, marginBottom: isMobile ? 12 : 18 }}>
                  {IconComp && <IconComp style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24 }} />}
                </div>
                <div style={{ fontWeight: 800, fontSize: isMobile ? 13 : 16, color: "#0f172a", marginBottom: isMobile ? 0 : 10, lineHeight: 1.3 }}>{f.title}</div>
                {!isMobile && <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>{f.description}</div>}
                <div className="accent-bar" style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(to right, ${f.color}, #60a5fa)`, transform: "scaleX(0)", transformOrigin: "left", transition: "transform 0.3s ease" }} />
              </div>
            );
          })}
        </div>

        {/* ── Bottom CTA banner — "Contact Us" ── */}
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", borderRadius: 24, padding: isMobile ? "40px 24px" : isTablet ? "48px 36px" : "56px 48px", marginTop: isMobile ? 32 : 48, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)", backgroundSize: "40px 40px" }} />
          <div style={{ position: "relative" }}>
            <h3 style={{ fontSize: isMobile ? 22 : 34, fontWeight: 900, color: "white", marginBottom: 14 }}>{d.ctaHeadline}</h3>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: isMobile ? 14 : 17, maxWidth: 520, margin: "0 auto 32px", lineHeight: 1.7 }}>{d.ctaDescription}</p>
            <button
              onClick={cta.contactUs}
              style={{ background: "#1d4ed8", color: "white", border: "none", padding: isMobile ? "12px 28px" : "14px 36px", borderRadius: 12, fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, transition: "all 0.25s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#1e3a8a"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#1d4ed8"; e.currentTarget.style.transform = "none"; }}>
              {d.ctaButton} <ArrowRight style={{ width: 17, height: 17 }} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FlexOfficeFeatures;